import { ContentType, SavedSearch, SavedSearchPage } from '@creditsights/api-ui-web';
import { Button, Divider, ICONS, Icon } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { format } from 'date-fns';
import { isEmpty, sumBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { DEFAULT_DEBOUNCE_RATE, ROUTES } from '../../Base.constants';
import {
  EVENT_DRIVEN_REPORT,
  EVENT_TITLE_TAGS,
  getSearchFilters,
  transformFilter,
} from '../../components/FiltersBar/constants';
import FiltersSideBar from '../../components/FiltersSideBar';
import SaveSearchButton from '../../components/SaveSearchButton/SaveSearchButton';
import SavedSearchesDropdown from '../../components/SavedSearchesDropdown/SavedSearchesDropdown';
import { SELECTED_SAVED_SEARCH_URL_KEY } from '../../components/SavedSearchesDropdown/SavedSearchesDropdown.constants';
import { useLocalStorage } from '../../hooks/common';
import { useFetchIssuersByIds } from '../../hooks/issuers';
import useFetchUserPermissions from '../../hooks/permissions/useFetchUserPermissions';
import useFetchSearch, { SearchContentType } from '../../hooks/searches/useFetchSearch';
import { Filters, IssuerOption, PageType } from '../../models';
import { SortOption } from '../../services/services.constants';
import { DATE_FORMATS } from '../../utils/dateUtils';
import { RELATIVE_DATE_RANGE_LABELS } from '../MyContent/SearchesTab/CellRenderers/FiltersRenderer/FiltersRenderer.logic';
import NoSearchResults from './NoSearchResults/NoSearchResults';
import {
  DEFAULT_SECTION_ORDER_CONFIG,
  SEARCH_RESULT_DEFAULT_SORT_OPTION,
  SEARCH_TERM_URL_KEY,
  SECTION_ID,
  SearchResultCategory,
  SearchResultCategoryToContentTypeFilterMap,
  excludedFilterChips,
} from './SearchResults.constants';
import {
  SortSearchKey,
  findDisplayName,
  getUrlSearchParams,
  searchFilterOptions,
  updateTopicsSearchTermQuery,
} from './SearchResults.logic';
import styles from './SearchResults.module.scss';
import SearchResultsHeader from './SearchResultsHeader/SearchResultsHeader';
import ArticlesSearchResultTable from './searchResultsTables/ArticlesSearchResultTable/ArticlesSearchResultTable';
import CompaniesTable from './searchResultsTables/CompaniesTable/CompaniesTable';
import { SEARCH_RESULT_TABLES_PAGINATION } from './searchResultsTables/searchResultTables.logic';
import { sanitizeElasticSearchStringValue } from '../../utils/sanitizeElasticSearchStringValue';

const cx = classNames.bind(styles);

const SearchResults = (): React.ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get(SEARCH_TERM_URL_KEY)?.trim() || '';
  const [sortSearch, setSortSearch] = useLocalStorage(SortSearchKey, SEARCH_RESULT_DEFAULT_SORT_OPTION);
  const activeSortOption: SortOption = (searchParams.get('sort') as SortOption) || sortSearch;
  const validCategories = Object.values(SearchResultCategory);
  const activeCategory: SearchResultCategory =
    (searchParams.get('category') as SearchResultCategory) || SearchResultCategory.All;
  const activeFilterType = SearchResultCategoryToContentTypeFilterMap[activeCategory];
  const [filters, setFilters] = useState<Filters | null>(null);
  const [filterChips, setFilterChips] = useState<FilterChip[]>([]);
  const [allFiltersData, setAllFiltersData] = useState<any>();
  const [resetKey, setResetKey] = useState<string[]>([]);
  const { data: selectedIssuers, isLoading } = useFetchIssuersByIds(filters?.tags?.[IssuerOption] ?? []);

  useEffect(() => {
    copyFiltersToChips();
  }, [isLoading]);

  const tagsSearchList = searchParams.get('tagsList')?.split('+') || [];

  interface FilterChip {
    tagId?: string;
    title: string;
    filter?: string;
  }

  const copyFiltersToChips = () => {
    let filteredArr: FilterChip[] = [];

    // Process issuer filters separately
    if (filters?.tags && filters.tags[IssuerOption]) {
      const issuerChips = filters.tags[IssuerOption].map((tagId) => ({
        filter: IssuerOption,
        tagId,
        title: selectedIssuers?.find((item) => item.tagId === tagId)?.displayName || '',
      })).filter((item) => !!item.title);
      filteredArr.push(...issuerChips);
    }

    // Process other filter types
    for (const [key, value] of Object.entries(filters?.tags || {})) {
      if (key !== IssuerOption) {
        const filterChips = value
          .filter((tag) => tag !== EVENT_DRIVEN_REPORT)
          .map((tag) => ({
            filter: key,
            tagId: tag,
            title: findDisplayName(searchFilterOptions(key, allFiltersData), tag),
          }));
        filteredArr.push(...filterChips);
      }
    }

    // Process other filter conditions
    if (filters?.titleContains) {
      const titleChips = filters.titleContains.map((title) => ({
        filter: 'source',
        tagId: EVENT_TITLE_TAGS[title][1],
        title: EVENT_TITLE_TAGS[title][0],
      }));
      filteredArr.push(...titleChips);
    }
    if (filters?.relativeRange) {
      filteredArr.push({
        filter: 'Date',
        tagId: 'relative',
        title: RELATIVE_DATE_RANGE_LABELS[filters.relativeRange],
      });
    }
    if (!filters?.relativeRange && filters?.startDate) {
      filteredArr.push({
        filter: 'Date',
        tagId: 'start',
        title: `From ${format(filters.startDate, DATE_FORMATS.YEAR_MONTH_DAY)}`,
      });
    }
    if (!filters?.relativeRange && filters?.endDate) {
      filteredArr.push({
        filter: 'Date',
        tagId: 'end',
        title: `To ${format(filters.endDate, DATE_FORMATS.YEAR_MONTH_DAY)}`,
      });
    }
    filteredArr = filteredArr.filter(
      (el) => el.tagId && typeof el.tagId === 'string' && !excludedFilterChips.includes(el.tagId)
    );
    setFilterChips(filteredArr);
  };

  const setFiltersDebounced = useDebouncedCallback((filters: Filters | null, data: any) => {
    setFilters(filters || {});
    setAllFiltersData(data);
    copyFiltersToChips();
  }, DEFAULT_DEBOUNCE_RATE);

  useEffect(() => {
    setPaginationByActiveCategory(activeCategory);
  }, [activeCategory]);

  const queriesEnabled = (!!searchTerm && filters !== null) || tagsSearchList?.length > 0;

  const [companiesShowItemsCount, setCompaniesShowItemsCount] = useState(SEARCH_RESULT_TABLES_PAGINATION.defaultOption);
  const [companiesPage, setCompaniesPage] = useState(1);
  const resetCompaniesPage = () => setCompaniesPage(1);
  const companiesQuery = useFetchSearch(
    SearchContentType.companies,
    activeCategory,
    filters,
    companiesShowItemsCount,
    companiesPage,
    searchTerm,
    undefined,
    queriesEnabled && [SearchResultCategory.All, SearchResultCategory.Companies].includes(activeCategory)
  );

  const [newsShowItemsCount, setNewsShowItemsCount] = useState(SEARCH_RESULT_TABLES_PAGINATION.defaultOption);
  const [newsPage, setNewsPage] = useState(1);
  const resetNewsPage = () => setNewsPage(1);

  const updatedSearchTerm = (category: SearchResultCategory) => {
    switch (category) {
      case SearchResultCategory.Titles:
        return `content.title: (${sanitizeElasticSearchStringValue(searchTerm)})`;
      case SearchResultCategory.Topics:
        return updateTopicsSearchTermQuery(searchTerm, tagsSearchList);
      default: {
        return searchTerm;
      }
    }
  };

  const newsQuery = useFetchSearch(
    SearchContentType.news,
    activeCategory,
    filters,
    newsShowItemsCount,
    newsPage,
    updatedSearchTerm(activeCategory),
    activeSortOption,
    queriesEnabled &&
      [
        SearchResultCategory.All,
        SearchResultCategory.News,
        SearchResultCategory.Titles,
        SearchResultCategory.Topics,
      ].includes(activeCategory)
  );

  const [covenantsShowItemsCount, setCovenantsShowItemsCount] = useState(SEARCH_RESULT_TABLES_PAGINATION.defaultOption);
  const [covenantsPage, setCovenantsPage] = useState(1);
  const resetCovenantsPage = () => setCovenantsPage(1);
  const covenantsQuery = useFetchSearch(
    SearchContentType.covenants,
    activeCategory,
    filters,
    covenantsShowItemsCount,
    covenantsPage,
    updatedSearchTerm(activeCategory),
    activeSortOption,
    queriesEnabled &&
      [
        SearchResultCategory.All,
        SearchResultCategory.Covenants,
        SearchResultCategory.Titles,
        SearchResultCategory.Topics,
      ].includes(activeCategory)
  );

  const [researchShowItemsCount, setResearchShowItemsCount] = useState(SEARCH_RESULT_TABLES_PAGINATION.defaultOption);
  const [researchPage, setResearchPage] = useState(1);
  const resetResearchPage = () => setResearchPage(1);
  const researchQuery = useFetchSearch(
    SearchContentType.research,
    activeCategory,
    filters,
    researchShowItemsCount,
    researchPage,
    updatedSearchTerm(activeCategory),
    activeSortOption,
    queriesEnabled &&
      [
        SearchResultCategory.All,
        SearchResultCategory.Research,
        SearchResultCategory.Titles,
        SearchResultCategory.Topics,
      ].includes(activeCategory)
  );
  useEffect(() => {
    copyFiltersToChips();
    resetCompaniesPage();
    resetNewsPage();
    resetCovenantsPage();
    resetResearchPage();
  }, [filters, searchTerm, activeCategory]);

  useEffect(() => {
    resetNewsPage();
    resetCovenantsPage();
    resetResearchPage();
  }, [activeSortOption]);

  const { data: userPermissions } = useFetchUserPermissions();

  const noResultsForAllCategories =
    filters !== null &&
    !researchQuery.isLoading &&
    !researchQuery.data?.length &&
    !covenantsQuery.isLoading &&
    !covenantsQuery.data?.length &&
    !newsQuery.isLoading &&
    !newsQuery.data?.length;

  const noResultsForCompaniesCategory = !companiesQuery.isLoading && !companiesQuery.data?.length;
  const companiesQueryFailed = companiesQuery.isError;

  const searchResultFailed = researchQuery.isError || covenantsQuery.isError || newsQuery.isError;

  const shouldDisplayNoResultsMessage =
    ((activeCategory === SearchResultCategory.Topics || activeCategory === SearchResultCategory.Titles) &&
      noResultsForAllCategories &&
      !searchResultFailed) ||
    (activeCategory === SearchResultCategory.All &&
      noResultsForAllCategories &&
      !searchResultFailed &&
      noResultsForCompaniesCategory &&
      !companiesQueryFailed) ||
    (activeCategory === SearchResultCategory.Companies && noResultsForCompaniesCategory && !companiesQueryFailed);

  const canSaveSearches = userPermissions?.hasAccessToMyContentSearches();

  const resultsCount = useMemo(() => {
    const categoryQueryMap = {
      [SearchResultCategory.Companies]: companiesQuery,
      [SearchResultCategory.Research]: researchQuery,
      [SearchResultCategory.Covenants]: covenantsQuery,
      [SearchResultCategory.News]: newsQuery,
    };

    if (activeCategory === SearchResultCategory.All) {
      const queries = Object.values(categoryQueryMap);
      const isAnyCategoryLoading = queries.some((query) => query.isLoading);

      return isAnyCategoryLoading
        ? undefined
        : sumBy(Object.values(categoryQueryMap), (query) => query.totalResults ?? 0);
    }

    const activeQuery = categoryQueryMap[activeCategory];

    if (activeQuery?.isLoading) {
      return undefined;
    }

    return activeQuery?.totalResults ?? 0;
  }, [activeCategory, companiesQuery, researchQuery, covenantsQuery, newsQuery]);
  const resetTablesState = () => {
    setResearchShowItemsCount(SEARCH_RESULT_TABLES_PAGINATION.defaultOption);
    setCompaniesShowItemsCount(SEARCH_RESULT_TABLES_PAGINATION.defaultOption);
    setNewsShowItemsCount(SEARCH_RESULT_TABLES_PAGINATION.defaultOption);
    setCovenantsShowItemsCount(SEARCH_RESULT_TABLES_PAGINATION.defaultOption);
    resetResearchPage();
    resetCompaniesPage();
    resetNewsPage();
    resetCovenantsPage();
  };

  const setPaginationByActiveCategory = (activeCategory: string) => {
    switch (activeCategory) {
      case SearchResultCategory.Companies:
        setCompaniesShowItemsCount(SEARCH_RESULT_TABLES_PAGINATION.defaultExtendedOption);
        resetCompaniesPage();
        break;
      case SearchResultCategory.News:
        setNewsShowItemsCount(SEARCH_RESULT_TABLES_PAGINATION.defaultExtendedOption);
        resetNewsPage();
        break;
      case SearchResultCategory.Covenants:
        setCovenantsShowItemsCount(SEARCH_RESULT_TABLES_PAGINATION.defaultExtendedOption);
        resetCovenantsPage();
        break;
      case SearchResultCategory.Research:
        setResearchShowItemsCount(SEARCH_RESULT_TABLES_PAGINATION.defaultExtendedOption);
        resetResearchPage();
        break;
      default:
        resetTablesState();
    }
  };

  const onSavedSearchSelect = (search: SavedSearch) => {
    // prevents duplicate api requests until filters are set after debounce
    setFilters(null);
    copyFiltersToChips();
    /* when "filters" and "dateRange" between different saved searches are the same
       FiltersBar component does not trigger "onChange".
       When we set filters to "null" loader is displayed until new filters are set.
       Therefore we fallback here to set filters to current value in case "onChange" is not triggered
       to prevent infinite loader.
    */
    setFiltersDebounced(filters, allFiltersData);

    const newSearchParams = getUrlSearchParams(search);
    newSearchParams.set(SELECTED_SAVED_SEARCH_URL_KEY, search.id);
    setSearchParams(newSearchParams);
  };

  const onSortOptionSelect = (sortOptionValue: SortOption) => {
    setSortSearch(sortOptionValue);
    searchParams.set('sort', sortOptionValue);
    setSearchParams(searchParams);
  };

  const onCategorySelect = (category: string) => {
    if (category !== activeCategory) {
      searchParams.set('category', category);
      setSearchParams(searchParams);
    }
  };

  if (!validCategories.includes(activeCategory)) {
    return <Navigate to={`${ROUTES.search}?searchTerm=${searchTerm}`} replace />;
  }

  if (isEmpty(searchTerm) && tagsSearchList?.length === 0) {
    return <Navigate to={ROUTES.notFound} replace />;
  }

  const onResetChip = (chip) => {
    setResetKey([chip.filter, chip.tagId]);
  };
  const renderChip = (chip) => {
    return (
      <div className={cx('chip-item')} key={chip.tagId}>
        <span data-testid={`${chip.title}-chip`} className={cx('chip-item-title')}>
          {chip.title}
        </span>
        <Button className={cx('filter-reset')} onClick={() => onResetChip(chip)}>
          <Icon name={ICONS.Clear} width={12} height={12} />
        </Button>
      </div>
    );
  };
  const renderSection = (sectionId: SECTION_ID) => {
    switch (sectionId) {
      case SECTION_ID.COMPANIES:
        return (
          <CompaniesTable
            key={sectionId}
            query={companiesQuery}
            showItemsCount={companiesShowItemsCount}
            onCategorySelect={() => {
              onCategorySelect(SearchResultCategory.Companies);
            }}
            onShowItemsCountChange={(count) => {
              setCompaniesShowItemsCount(count);
              resetCompaniesPage();
            }}
            activeCategory={activeCategory}
            tableCategory={SearchResultCategory.Companies}
            page={companiesPage}
            setPage={setCompaniesPage}
          />
        );
      case SECTION_ID.RESEARCH:
        return (
          <ArticlesSearchResultTable
            key={sectionId}
            contentType={ContentType.Research}
            query={researchQuery}
            showItemsCount={researchShowItemsCount}
            onShowItemsCountChange={(count) => {
              setResearchShowItemsCount(count);
              resetResearchPage();
            }}
            onCategorySelect={() => {
              onCategorySelect(SearchResultCategory.Research);
            }}
            activeCategory={activeCategory}
            tableCategory={SearchResultCategory.Research}
            page={researchPage}
            setPage={setResearchPage}
          />
        );
      case SECTION_ID.NEWS:
        return (
          <ArticlesSearchResultTable
            key={sectionId}
            contentType={ContentType.News}
            query={newsQuery}
            showItemsCount={newsShowItemsCount}
            onShowItemsCountChange={(count) => {
              setNewsShowItemsCount(count);
              resetNewsPage();
            }}
            onCategorySelect={() => {
              onCategorySelect(SearchResultCategory.News);
            }}
            activeCategory={activeCategory}
            tableCategory={SearchResultCategory.News}
            page={newsPage}
            setPage={setNewsPage}
          />
        );
      case SECTION_ID.COVENANTS:
        return (
          <ArticlesSearchResultTable
            key={sectionId}
            contentType={ContentType.Covenants}
            query={covenantsQuery}
            showItemsCount={covenantsShowItemsCount}
            onShowItemsCountChange={(count) => {
              setCovenantsShowItemsCount(count);
              resetCovenantsPage();
            }}
            onCategorySelect={() => {
              onCategorySelect(SearchResultCategory.Covenants);
            }}
            activeCategory={activeCategory}
            tableCategory={SearchResultCategory.Covenants}
            page={covenantsPage}
            setPage={setCovenantsPage}
          />
        );
      default: {
        return null;
      }
    }
  };
  const renderSearchResultSections = () => {
    const sectionIds = DEFAULT_SECTION_ORDER_CONFIG(userPermissions!.userSubscription);
    return sectionIds.map((sectionId) => renderSection(sectionId));
  };

  return (
    <div className={cx('body')}>
      <div className={cx('side-filter-container')}>
        <div className={cx('filter-heading')}>Filter By</div>
        <Divider className={cx('divider')} />
        <FiltersSideBar
          onChange={setFiltersDebounced}
          resetKey={resetKey}
          filterTypes={getSearchFilters(activeCategory, PageType.Search)}
          transformFilter={transformFilter}
          articleType={activeFilterType}
          onChangeResetKey={() => setResetKey([])}
        />
        <Divider />
        <div className={cx('save-searches', 'secondary-tools')}>
          {canSaveSearches && (
            <div className={cx('save-searches', 'save-tools')}>
              <SaveSearchButton
                filters={filters}
                searchTerm={searchTerm}
                tab={activeCategory}
                sort={activeSortOption}
                searchResultsCount={resultsCount}
                pageName={SavedSearchPage.GlobalSearch}
              />
              <SavedSearchesDropdown onSearchSelect={onSavedSearchSelect} pageName={SavedSearchPage.GlobalSearch} />
            </div>
          )}
        </div>
      </div>
      <div className={cx('results-container')}>
        <SearchResultsHeader
          searchTerm={searchTerm}
          activeSortOption={activeSortOption}
          resultsCount={resultsCount}
          onSortOptionSelect={onSortOptionSelect}
        />
        <div className={cx('filter-chips')}>{filterChips.map((chip) => renderChip(chip))}</div>
        <div className={cx('widgets')}>
          {shouldDisplayNoResultsMessage && <NoSearchResults />}
          {(searchTerm || tagsSearchList.length > 0) && renderSearchResultSections()}
        </div>
      </div>
    </div>
  );
};

export default SearchResults;
